import { useCallback, useEffect, useRef, useState } from "react";

import type { SlotMetrics, VideoApi, VideoLatencyMode } from "../modules/teleop-session";

export interface UseVideoSlotsResult {
  streams: Record<number, MediaStream | null>;
  metrics: Record<number, SlotMetrics>;
  requestKeyframes: (slot: number) => void;
  restoreSlot: (slot: number) => boolean;
  setSlotEnabled: (slot: number, enabled: boolean) => void;
  setLatencyMode: (slot: number, mode: VideoLatencyMode) => void;
  startLatencyComparison: (slot: number) => boolean;
  cancelLatencyComparison: (slot: number) => void;
}

/**
 * Data source for the video grid. This hook holds NO DOM: it subscribes to the
 * transport module's per-slot MediaStreams + RTP metrics and exposes them so React
 * components can render. Rendering the `<video>` (srcObject/play/pause, overlay,
 * FPS, stall recovery) is the job of the components (see VideoGrid/VideoCard).
 */
export function useVideoSlots({ videoApi }: { videoApi: VideoApi | null }): UseVideoSlotsResult {
  const [streams, setStreams] = useState<Record<number, MediaStream | null>>({});
  const [metrics, setMetrics] = useState<Record<number, SlotMetrics>>({});

  const videoApiRef = useRef(videoApi);
  videoApiRef.current = videoApi;

  useEffect(() => {
    if (!videoApi?.onSlotStream || !videoApi?.onSlotMetrics) return undefined;
    const unsubStream = videoApi.onSlotStream((slot, stream) => {
      setStreams((prev) => ({ ...prev, [slot]: stream }));
    });
    const unsubMetrics = videoApi.onSlotMetrics((slot, m) => {
      setMetrics((prev) => ({ ...prev, [slot]: m }));
    });
    return () => {
      unsubStream();
      unsubMetrics();
    };
  }, [videoApi]);

  const requestKeyframes = useCallback((slot: number) => {
    videoApiRef.current?.requestKeyframes?.(slot);
  }, []);

  const restoreSlot = useCallback((slot: number) => {
    return videoApiRef.current?.restoreSlot?.(slot) ?? false;
  }, []);

  const setSlotEnabled = useCallback((slot: number, enabled: boolean) => {
    videoApiRef.current?.setSlotEnabled?.(slot, enabled);
    if (enabled) videoApiRef.current?.requestKeyframes?.(slot);
  }, []);

  const setLatencyMode = useCallback((slot: number, mode: VideoLatencyMode) => {
    videoApiRef.current?.setLatencyMode?.(slot, mode);
  }, []);
  const startLatencyComparison = useCallback((slot: number) => {
    return videoApiRef.current?.startLatencyComparison?.(slot) ?? false;
  }, []);
  const cancelLatencyComparison = useCallback((slot: number) => {
    videoApiRef.current?.cancelLatencyComparison?.(slot);
  }, []);

  return {
    streams,
    metrics,
    requestKeyframes,
    restoreSlot,
    setSlotEnabled,
    setLatencyMode,
    startLatencyComparison,
    cancelLatencyComparison,
  };
}
